import React, { useState, useEffect } from 'react';
import {
    X, Clock, CheckCircle, XCircle, AlertCircle, Award,
    TrendingUp, TrendingDown, Pause, Target, BarChart3,
    FileText, RefreshCw, ChevronDown, ChevronUp, Timer,
    HelpCircle, Check, Minus
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import ProgressRing from './ProgressRing';
import BarChart from './BarChart';

const TaskDetailModal = ({ employeeId, assignmentId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [expandedQuestions, setExpandedQuestions] = useState({});

    useEffect(() => {
        if (employeeId && assignmentId) {
            fetchTaskDetails();
        }
    }, [employeeId, assignmentId]);

    const fetchTaskDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(
                `/analytics/employees/${employeeId}/tasks/${assignmentId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setData(response.data.assignment);
        } catch (error) {
            console.error('Error fetching task details:', error);
            toast.error('Failed to load task details');
        } finally {
            setLoading(false);
        }
    };

    const toggleQuestion = (index) => {
        setExpandedQuestions(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const formatTime = (minutes) => {
        if (!minutes) return '-';
        if (minutes < 60) return `${Math.round(minutes)}m`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
            in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
            pending: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Minus },
            overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
            failed: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
            pending_review: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock }
        };
        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;
        return (
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
                <Icon className="w-4 h-4" />
                {status.replace(/_/g, ' ')}
            </span>
        );
    };

    const getComparisonBadge = (comparison, type = 'time') => {
        if (!comparison) return null;
        const badges = {
            fast: { bg: 'bg-green-100', text: 'text-green-700', icon: TrendingDown, label: 'Faster than average' },
            slow: { bg: 'bg-red-100', text: 'text-red-700', icon: TrendingUp, label: 'Slower than average' },
            average: { bg: 'bg-gray-100', text: 'text-gray-600', icon: Minus, label: 'Average' },
            above_average: { bg: 'bg-green-100', text: 'text-green-700', icon: TrendingUp, label: 'Above average' },
            below_average: { bg: 'bg-red-100', text: 'text-red-700', icon: TrendingDown, label: 'Below average' }
        };
        const badge = badges[comparison] || badges.average;
        const Icon = badge.icon;
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                <Icon className="w-3 h-3" />
                {badge.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
                <div className="bg-white rounded-xl p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="text-gray-500 mt-4">Loading task details...</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const { task, performance, timeBreakdown, comparison, questionBreakdown, attemptHistory, sectionDetails } = data;

    // Calculate question stats
    const correctAnswers = questionBreakdown?.filter(q => q.isCorrect === true).length || 0;
    const incorrectAnswers = questionBreakdown?.filter(q => q.isCorrect === false).length || 0;
    const totalQuestions = questionBreakdown?.length || 0;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{task.title}</h2>
                            <div className="flex items-center gap-3 mt-2">
                                {getStatusBadge(data.status)}
                                <span className="text-sm text-gray-500 capitalize">{task.taskType}</span>
                                {task.category && (
                                    <span className="text-sm text-gray-500">• {task.category}</span>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Performance Overview */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Score */}
                        {performance.score !== null && (
                            <div className="bg-gray-50 rounded-xl p-4 text-center">
                                <div className="flex items-center justify-center mb-2">
                                    <Award className={`w-6 h-6 ${performance.score >= task.passingScore ? 'text-green-500' : 'text-red-500'
                                        }`} />
                                </div>
                                <p className={`text-2xl font-bold ${performance.score >= task.passingScore ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                    {performance.score}%
                                </p>
                                <p className="text-xs text-gray-500">Score (Pass: {task.passingScore}%)</p>
                                {comparison.scoreComparison && (
                                    <div className="mt-2">
                                        {getComparisonBadge(comparison.scoreComparison, 'score')}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Time */}
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <div className="flex items-center justify-center mb-2">
                                <Clock className="w-6 h-6 text-blue-500" />
                            </div>
                            <p className="text-2xl font-bold text-gray-900">
                                {formatTime(timeBreakdown.totalMinutes)}
                            </p>
                            <p className="text-xs text-gray-500">
                                Time Spent {task.estimatedDuration && `(Est: ${task.estimatedDuration}m)`}
                            </p>
                            {comparison.timeComparison && (
                                <div className="mt-2">
                                    {getComparisonBadge(comparison.timeComparison, 'time')}
                                </div>
                            )}
                        </div>

                        {/* Attempts */}
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <div className="flex items-center justify-center mb-2">
                                <RefreshCw className="w-6 h-6 text-purple-500" />
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{performance.attempts}</p>
                            <p className="text-xs text-gray-500">
                                Attempt{performance.attempts !== 1 ? 's' : ''}
                                {task.maxAttempts && ` (Max: ${task.maxAttempts})`}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                Avg: {comparison.companyAvg.avgAttempts}
                            </p>
                        </div>

                        {/* Pauses */}
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <div className="flex items-center justify-center mb-2">
                                <Pause className="w-6 h-6 text-amber-500" />
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{performance.pauseCount}</p>
                            <p className="text-xs text-gray-500">
                                Pauses ({performance.autoPauseCount} auto)
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                Avg: {comparison.companyAvg.avgPauses}
                            </p>
                        </div>
                    </div>

                    {/* Company Comparison */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                            <BarChart3 className="w-5 h-5" />
                            Company Comparison
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <p className="text-blue-600">Avg Time</p>
                                <p className="font-semibold text-blue-900">{formatTime(comparison.companyAvg.timeMinutes)}</p>
                            </div>
                            {comparison.companyAvg.score > 0 && (
                                <div>
                                    <p className="text-blue-600">Avg Score</p>
                                    <p className="font-semibold text-blue-900">{comparison.companyAvg.score}%</p>
                                </div>
                            )}
                            <div>
                                <p className="text-blue-600">Score Range</p>
                                <p className="font-semibold text-blue-900">
                                    {comparison.companyAvg.minScore}% - {comparison.companyAvg.maxScore}%
                                </p>
                            </div>
                            <div>
                                <p className="text-blue-600">Total Attempts</p>
                                <p className="font-semibold text-blue-900">{comparison.companyAvg.totalAttempts}</p>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <h3 className="font-semibold text-gray-800 mb-4">Timeline</h3>
                        <div className="space-y-3">
                            {[
                                { label: 'Assigned', date: data.dates.assigned, icon: FileText },
                                { label: 'First Viewed', date: data.dates.firstViewed, icon: Target },
                                { label: 'Started', date: data.dates.started, icon: Clock },
                                { label: 'Submitted', date: data.dates.submitted, icon: CheckCircle },
                                { label: 'Completed', date: data.dates.completed, icon: CheckCircle },
                                { label: 'Due Date', date: data.dates.due, icon: AlertCircle, highlight: true }
                            ].filter(item => item.date).map((item, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${item.highlight ? 'bg-amber-100' : 'bg-gray-100'}`}>
                                        <item.icon className={`w-4 h-4 ${item.highlight ? 'text-amber-600' : 'text-gray-600'}`} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-700">{item.label}</p>
                                    </div>
                                    <p className="text-sm text-gray-500">{formatDate(item.date)}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Quiz Question Breakdown */}
                    {questionBreakdown && questionBreakdown.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <div className="p-4 border-b border-gray-200 bg-gray-50">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-semibold text-gray-800">Question-by-Question Breakdown</h3>
                                    <div className="flex items-center gap-4 text-sm">
                                        <span className="flex items-center gap-1 text-green-600">
                                            <CheckCircle className="w-4 h-4" />
                                            {correctAnswers} correct
                                        </span>
                                        <span className="flex items-center gap-1 text-red-600">
                                            <XCircle className="w-4 h-4" />
                                            {incorrectAnswers} incorrect
                                        </span>
                                    </div>
                                </div>
                                {/* Progress bar */}
                                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden flex">
                                    <div
                                        className="bg-green-500 h-full"
                                        style={{ width: `${(correctAnswers / totalQuestions) * 100}%` }}
                                    />
                                    <div
                                        className="bg-red-500 h-full"
                                        style={{ width: `${(incorrectAnswers / totalQuestions) * 100}%` }}
                                    />
                                </div>
                            </div>

                            <div className="divide-y divide-gray-100">
                                {questionBreakdown.map((question, index) => (
                                    <div key={index} className="p-4">
                                        <div
                                            className="flex items-start gap-3 cursor-pointer"
                                            onClick={() => toggleQuestion(index)}
                                        >
                                            <div className={`p-2 rounded-lg flex-shrink-0 ${question.isCorrect === true ? 'bg-green-100' :
                                                    question.isCorrect === false ? 'bg-red-100' : 'bg-gray-100'
                                                }`}>
                                                {question.isCorrect === true ? (
                                                    <Check className="w-4 h-4 text-green-600" />
                                                ) : question.isCorrect === false ? (
                                                    <X className="w-4 h-4 text-red-600" />
                                                ) : (
                                                    <HelpCircle className="w-4 h-4 text-gray-500" />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900">
                                                    Q{index + 1}: {question.questionText}
                                                </p>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {question.questionType.replace(/_/g, ' ')}
                                                    {question.points && ` • ${question.points} point${question.points !== 1 ? 's' : ''}`}
                                                </p>
                                            </div>
                                            {expandedQuestions[index] ? (
                                                <ChevronUp className="w-5 h-5 text-gray-400" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-400" />
                                            )}
                                        </div>

                                        {expandedQuestions[index] && (
                                            <div className="mt-4 ml-11 space-y-3">
                                                {/* Options (if multiple choice) */}
                                                {question.options && (
                                                    <div className="space-y-2">
                                                        <p className="text-xs font-medium text-gray-500 uppercase">Options</p>
                                                        {question.options.map((option, optIndex) => {
                                                            const isUserAnswer = String(question.userAnswer) === String(optIndex) ||
                                                                question.userAnswer === option;
                                                            const isCorrectAnswer = String(question.correctAnswer) === String(optIndex) ||
                                                                question.correctAnswer === option;

                                                            return (
                                                                <div
                                                                    key={optIndex}
                                                                    className={`p-2 rounded-lg text-sm flex items-center gap-2 ${isCorrectAnswer ? 'bg-green-50 border border-green-200' :
                                                                            isUserAnswer && !isCorrectAnswer ? 'bg-red-50 border border-red-200' :
                                                                                'bg-gray-50'
                                                                        }`}
                                                                >
                                                                    {isCorrectAnswer && <Check className="w-4 h-4 text-green-600" />}
                                                                    {isUserAnswer && !isCorrectAnswer && <X className="w-4 h-4 text-red-600" />}
                                                                    <span className={
                                                                        isCorrectAnswer ? 'text-green-700' :
                                                                            isUserAnswer ? 'text-red-700' : 'text-gray-700'
                                                                    }>
                                                                        {typeof option === 'object' ? option.text || option.label : option}
                                                                    </span>
                                                                    {isUserAnswer && <span className="text-xs text-gray-500 ml-auto">(Selected)</span>}
                                                                    {isCorrectAnswer && <span className="text-xs text-green-600 ml-auto">(Correct)</span>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* User's answer (for non-multiple choice) */}
                                                {!question.options && question.userAnswer && (
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">User's Answer</p>
                                                        <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                                                            {String(question.userAnswer)}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Correct answer */}
                                                {!question.options && question.correctAnswer && (
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Correct Answer</p>
                                                        <p className="text-sm text-green-700 bg-green-50 p-2 rounded">
                                                            {String(question.correctAnswer)}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Explanation */}
                                                {question.explanation && (
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Explanation</p>
                                                        <p className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
                                                            {question.explanation}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Attempt History */}
                    {attemptHistory && attemptHistory.length > 1 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <h3 className="font-semibold text-gray-800 mb-4">Attempt History</h3>
                            <div className="space-y-3">
                                {attemptHistory.map((attempt, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-gray-700">
                                                Attempt {attempt.attempt_number || index + 1}
                                            </span>
                                            {attempt.submitted_at && (
                                                <span className="text-xs text-gray-500">
                                                    {formatDate(attempt.submitted_at)}
                                                </span>
                                            )}
                                        </div>
                                        {attempt.score !== undefined && (
                                            <span className={`font-semibold ${attempt.score >= (task.passingScore || 70) ? 'text-green-600' : 'text-red-600'
                                                }`}>
                                                {attempt.score}%
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Score improvement chart */}
                            {attemptHistory.length > 1 && attemptHistory.every(a => a.score !== undefined) && (
                                <div className="mt-4">
                                    <p className="text-sm text-gray-500 mb-2">Score Progression</p>
                                    <BarChart
                                        data={attemptHistory.map((a, i) => ({
                                            label: `#${a.attempt_number || i + 1}`,
                                            value: a.score,
                                            color: a.score >= (task.passingScore || 70) ? '#22c55e' : '#ef4444'
                                        }))}
                                        height={100}
                                        maxValue={100}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Section Progress (for combined tasks) */}
                    {sectionDetails && sectionDetails.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl p-4">
                            <h3 className="font-semibold text-gray-800 mb-4">Section Progress</h3>
                            <div className="space-y-3">
                                {sectionDetails.map((section, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-medium text-gray-900">{section.title}</p>
                                            <p className="text-xs text-gray-500 capitalize">{section.type.replace(/_/g, ' ')}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {section.score !== undefined && (
                                                <span className="text-sm font-medium text-gray-700">{section.score}%</span>
                                            )}
                                            {getStatusBadge(section.status)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Admin Feedback */}
                    {data.adminFeedback && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <h3 className="font-semibold text-amber-800 mb-2">Admin Feedback</h3>
                            <p className="text-sm text-amber-700">{data.adminFeedback}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskDetailModal;
